/**
 * src/constants/effectivenessScale.js
 *
 * 1–5 control effectiveness / maturity scale. Separate from the binary
 * pass/fail gates — satisfies ISO 27001:2022 clause 9.1 (evaluation of
 * information security performance) and provides richer audit evidence.
 *
 * Scale aligns with the Capability Maturity Model (CMM) vocabulary adopted
 * by ISO/IEC 33000 and widely used in information security audits.
 */

export const EFFECTIVENESS_LEVELS = [
  {
    value: 1,
    label: 'Initial',
    short: '1 – Initial',
    color: '#f87171',
    description:
      'Ad-hoc and reactive. No documented process; outcomes depend on individual effort.',
  },
  {
    value: 2,
    label: 'Developing',
    short: '2 – Developing',
    color: '#fb923c',
    description: 'Basic controls exist but are applied inconsistently. Limited documentation.',
  },
  {
    value: 3,
    label: 'Defined',
    short: '3 – Defined',
    color: '#facc15',
    description: 'Documented, communicated, and consistently applied across the organisation.',
  },
  {
    value: 4,
    label: 'Managed',
    short: '4 – Managed',
    color: '#4ade80',
    description: 'Measured and monitored. Metrics tracked; deviations detected and corrected.',
  },
  {
    value: 5,
    label: 'Optimised',
    short: '5 – Optimised',
    color: '#34d399',
    description: 'Continuously improved. Best-practice aligned with lessons learned feeding back.',
  },
];

/**
 * The maturity level a tenant is aiming for, per framework.
 *
 * The goal is the thing the whole maturity screen measures from: how far the
 * system's evidence is from it, and how far the owner's own rating is from it.
 * It is scoped to a FRAMEWORK, not to a control — a control shared by ISO 27001
 * and NIS2 is answering to a different bar in each, so the pair (framework,
 * control) is what carries a goal. Never ask "what is this control's goal"
 * without a framework in hand; there is no single answer.
 *
 * Existing tenants have no `maturityTarget` on their framework records, so the
 * resolver supplies DEFAULT_MATURITY_TARGET — no migration is needed, and a
 * tenant that has never opened the setting behaves exactly as it did before.
 */
export const DEFAULT_MATURITY_TARGET = 3;

/** Resolve one framework's goal, clamped into the 1–5 scale. */
export function resolveMaturityTarget(framework) {
  const value = framework?.maturityTarget;
  // Number(null) is 0, not NaN — an explicit null must still mean "unset",
  // the same as undefined or a missing field, not "level 0" rounded up to 1.
  if (value == null) return DEFAULT_MATURITY_TARGET;
  const raw = Number(value);
  if (!Number.isFinite(raw)) return DEFAULT_MATURITY_TARGET;
  return Math.max(1, Math.min(EFFECTIVENESS_LEVELS.length, Math.round(raw)));
}

/** Look up a level by numeric score (1–5). Returns undefined for unscored (0 / null). */
export function getEffectivenessLevel(score) {
  return EFFECTIVENESS_LEVELS.find((l) => l.value === score);
}

/**
 * Compute framework-level maturity percentage from a set of REQUIREMENT keys
 * and the controlEffectiveness map.
 *
 * Keys are `${frameworkId}::${externalId}` (requirementRatings.js), because a
 * rating belongs to the requirement and not to the TCF control it maps onto
 * (CTRL-B-001) — passing control ids here reads a framework's own clauses as
 * one entry and returns a figure for the merged control rather than the
 * framework.
 *
 * Formula: (sum of rated scores) / (rated count * 5) * 100
 * Returns null when nothing has been rated yet. Only an explicit rating is in
 * the map at all, so a figure carried from another framework and not yet
 * adopted is correctly absent from both sides of the ratio (CTRL-B-002).
 */
export function computeFrameworkMaturity(requirementKeys, controlEffectiveness) {
  if (!requirementKeys?.length || !controlEffectiveness) return null;
  const rated = requirementKeys
    .map((id) => controlEffectiveness[id]?.score)
    .filter((s) => s >= 1 && s <= 5);
  if (!rated.length) return null;
  return Math.round((rated.reduce((a, b) => a + b, 0) / (rated.length * 5)) * 100);
}

/**
 * How long a rating stands without being looked at again (ISO 27001:2022
 * §9.1 — the organisation determines when monitoring and evaluation are
 * performed; Tryggr's cadence is annual). No control carries its own review
 * period today, so this is every control's period.
 *
 * This is the ONE definition of "the rating is stale". The maturity screen's
 * "over a year ago" flag and the assurance case's confidence clock both call
 * the helpers below, so the two surfaces can never disagree about the same
 * rating (GH #7168).
 */
export const EFFECTIVENESS_REVIEW_PERIOD_DAYS = 365;

const DAY_MS = 24 * 60 * 60 * 1000;

function instantMs(v) {
  if (v == null || v === '') return NaN;
  return typeof v === 'number' ? v : new Date(v).getTime();
}

/**
 * The last time someone looked at this rating: the later of when it was last
 * set (`ratedAt`) and when a §9.1 review last confirmed it unchanged
 * (`reviewedAt`, GH #5643). null when neither is a readable date.
 */
export function effectivenessLastLookedAt(entry) {
  const rated = instantMs(entry?.ratedAt);
  const reviewed = instantMs(entry?.reviewedAt);
  const best = Math.max(
    Number.isFinite(rated) ? rated : -Infinity,
    Number.isFinite(reviewed) ? reviewed : -Infinity
  );
  return Number.isFinite(best) ? new Date(best).toISOString() : null;
}

/**
 * The instant (ms) after which the rating is stale, or NaN when it carries no
 * readable date — which the callers treat as stale already (fail-safe: a
 * rating nobody can date is never presented as current).
 */
export function effectivenessStaleAfterMs(entry) {
  const last = instantMs(effectivenessLastLookedAt(entry));
  return Number.isFinite(last) ? last + EFFECTIVENESS_REVIEW_PERIOD_DAYS * DAY_MS : NaN;
}

/** True when the rating has not been looked at within its review period. */
export function isEffectivenessStale(entry, now = Date.now()) {
  const after = effectivenessStaleAfterMs(entry);
  const nowMs = instantMs(now);
  return !Number.isFinite(after) || !Number.isFinite(nowMs) || nowMs > after;
}

/**
 * A rating the browser has sent and the server has not yet stored (GH #6842,
 * #7849). It carries no `ratedAt`: that date is the server's stamp, and it is
 * also the token a "still accurate" confirmation is checked against
 * (controls-write RATING_MOVED). A browser clock standing in for it was a
 * token the server never held, so a confirmation sent before the save
 * returned was refused every time. Without a date the entry already reads as
 * stale and unconfirmable (fail-safe); `pending` says why. The marker never
 * leaves the browser — controlEffectiveness is not in the blob save, and the
 * per-record write sends only score and justification — and the server's
 * entry replaces it whole.
 */
export function pendingEffectivenessEntry({ score, justification, ratedBy }) {
  return { score, justification, ratedBy: ratedBy || null, pending: true };
}

/**
 * How long a rating write holds the background refresh off its record
 * (src/state/pendingRecordWrites.js). It must outlive the request: a refresh
 * that lands while the write is still unanswered replaces the pending entry
 * with the server's previous one, Confirm is offered against that old date,
 * and the reviewer's own new rating is then refused as "changed" (GH #6842).
 * The default 15s hold is shorter than the 20s+ writes seen on test (#7316).
 * An HTTP-triggered Function answers within the platform's 230 s response
 * limit (the same limit measurement-run plans around), so 240 s covers the
 * whole request; every caller releases the hold the moment it is answered.
 */
export const RATING_WRITE_HOLD_MS = 240000;

/** True while the rating's write has not been answered by the server. */
export function isEffectivenessPending(entry) {
  return entry?.pending === true;
}

/**
 * The ratings a refused confirmation clashed on: controls-write answers
 * RATING_MOVED with the CONC-01 `conflicts` list (err.conflicts), one entry per
 * requirement whose rating changed, carrying the stored rating as `theirs`.
 * Returns [{ key, theirs }], or null when the error is not that refusal or
 * names nothing (an older backend) — the caller then falls back to the
 * generic conflict path.
 */
export function ratingMovedConflicts(err) {
  if (err?.httpStatus !== 409 || err?.conflictReason !== 'RATING_MOVED') return null;
  const list = (Array.isArray(err.conflicts) ? err.conflicts : [])
    .filter(
      (c) =>
        c?.field === 'controlEffectiveness' &&
        Array.isArray(c.path) &&
        typeof c.path[1] === 'string'
    )
    .map((c) => ({ key: c.path[1], theirs: c.theirs || null }));
  return list.length ? list : null;
}

/**
 * The sentence that tells the reviewer which rating changed under them and
 * what it reads now (TRYGGR_INTENT.md §13: a same-field clash is named, never
 * a generic "updated elsewhere"). Nothing was recorded — the review is
 * all-or-nothing (GH #6849, decision A).
 */
export function describeRatingMoved(moved, labelFor = (k) => k) {
  const parts = (moved || []).map(({ key, theirs }) => {
    const level = theirs?.score ? getEffectivenessLevel(theirs.score) : null;
    const now = theirs?.score
      ? `now reads ${theirs.score}${level?.label ? ` · ${level.label}` : ''}`
      : 'has been removed';
    return `${labelFor(key)} ${now}`;
  });
  return `Not confirmed — a rating this review covers changed since you opened it: ${parts.join('; ')}. Review the current rating, then confirm again.`;
}
